import { useState, useMemo } from "react";
import { useInfiniteScroll } from "@/lib/api";
import { formatIndianTime, formatUtcTime } from "../lib/operatorFormatters";
import InfiniteListFooter from "./InfiniteListFooter";
import { Terminal, Filter, ShieldAlert, CheckCircle2, AlertTriangle } from "lucide-react";

const LEVEL_COLOR = {
  ERROR: "text-rose-400 font-bold bg-rose-950/60 border border-rose-900/60 px-1 rounded",
  WARNING: "text-amber-400 font-bold bg-amber-950/60 border border-amber-900/60 px-1 rounded",
  INFO: "text-emerald-400 font-semibold bg-emerald-950/40 border border-emerald-900/40 px-1 rounded",
};

export default function LogsPanel({ logs: rawLogs = [], listState = {} }) {
  const logs = useMemo(() => {
    if (Array.isArray(rawLogs)) return rawLogs;
    if (Array.isArray(rawLogs?.data)) return rawLogs.data;
    if (Array.isArray(rawLogs?.items)) return rawLogs.items;
    return [];
  }, [rawLogs]);
  const [levelFilter, setLevelFilter] = useState("ALL");
  const handleScroll = useInfiniteScroll(listState.loadMore || (() => {}), listState.hasMore);

  const filteredLogs = useMemo(() => {
    if (levelFilter === "ALL") return logs;
    return logs.filter((l) => (l.level || "").toUpperCase() === levelFilter);
  }, [logs, levelFilter]);

  return (
    <div className="bg-zinc-950 border border-zinc-800/80 rounded-xl shadow-2xl flex flex-col w-full h-[220px]" data-testid="logs-panel">
      {/* Header */}
      <div className="px-3.5 py-2 border-b border-zinc-800/70 flex items-center justify-between bg-zinc-900/40 rounded-t-xl">
        <div className="flex items-center gap-2">
          <div className="w-5 h-5 rounded bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center text-emerald-400">
            <Terminal size={12} />
          </div>
          <span className="text-xs font-bold uppercase tracking-wider text-zinc-200 font-mono">
            System Terminal Logs ({logs?.length || 0})
          </span>
        </div>

        {/* Level Filters */}
        <div className="flex items-center gap-1 text-[10px] font-mono">
          {["ALL", "INFO", "WARNING", "ERROR"].map((lvl) => (
            <button
              key={lvl}
              onClick={() => setLevelFilter(lvl)}
              className={`px-2 py-0.5 rounded transition-colors ${
                levelFilter === lvl
                  ? "bg-emerald-500/20 text-emerald-400 border border-emerald-500/40 font-bold"
                  : "bg-zinc-900/60 text-zinc-500 hover:text-zinc-300 border border-zinc-800/40"
              }`}
            >
              {lvl}
            </button>
          ))}
        </div>
      </div>

      {/* Terminal View */}
      <div className="infinite-scroll-container flex-1 px-3.5 py-2 font-mono text-[10px] leading-relaxed overflow-y-auto" onScroll={handleScroll}>
        {(!filteredLogs || filteredLogs.length === 0) && (
          <p className="text-zinc-600 py-4 text-center">awaiting log stream output…</p>
        )}
        {filteredLogs?.slice().reverse().map((l, i) => (
          <div key={l.id || `${l.ts || l.raw_ts || "log"}-${i}`} className="whitespace-nowrap flex items-center gap-2 py-0.5 border-b border-zinc-900/40 hover:bg-zinc-900/30 transition-colors">
            <span className="text-amber-300/80 font-mono shrink-0" title={`UTC: ${formatUtcTime(l.ts)}`}>{formatIndianTime(l.ts, { fallback: l.raw_ts ? "invalid" : "—" })}</span>
            <span className={`shrink-0 ${LEVEL_COLOR[l.level] || "text-zinc-500"}`}>[{l.level}]</span>
            {l.logger && <span className="text-sky-400 font-semibold shrink-0">[{l.logger}]</span>}
            <span className="text-zinc-300 truncate">{l.message}</span>
          </div>
        ))}
        {!!logs?.length && (
          <InfiniteListFooter
            loadingMore={listState.loadingMore}
            hasMore={listState.hasMore}
            error={listState.error}
          />
        )}
      </div>
    </div>
  );
}
